// Contas do story do Instagram (pages/CampanhaStory.js): onde cada produto
// começa, o bloco foto + folha e as mudanças de tamanho. Tudo em % do story
// (x/w da largura, y/h da altura), igual ao que o servidor desenha.

export const STORY_L = 1080, STORY_A = 1920;
export const PROP = STORY_A / STORY_L;        // altura / largura
export const MAX_PRODUTOS = 3;

// Onde os produtos ficam, por quantidade (px num story de 1080 x 1920; o topo
// padrão vai até ~600 e o período fica em ~1700). Cada vaga diz onde vai o preço:
// 'baixo' (folha embaixo da foto, `lado` dir/centro) ou 'dir'/'esq' (do lado);
// `folha` é a largura da folha em fração da vaga.
export const DISPOSICOES = {
  1: [
    { nome: 'grande', rotulo: 'Grande', vagas: [{ x: 70, y: 630, w: 940, h: 940, preco: 'baixo', lado: 'dir', folha: 0.6 }] },
    { nome: 'um-lado', rotulo: 'Preço do lado', vagas: [{ x: 30, y: 680, w: 1020, h: 860, preco: 'dir', folha: 0.5 }] },
  ],
  2: [
    { nome: 'lado-a-lado', rotulo: 'Lado a lado', vagas: [
      { x: 25, y: 680, w: 505, h: 880, preco: 'baixo', lado: 'centro', folha: 0.96 },
      { x: 550, y: 680, w: 505, h: 880, preco: 'baixo', lado: 'centro', folha: 0.96 }] },
    { nome: 'um-embaixo', rotulo: 'Um embaixo do outro', vagas: [
      { x: 40, y: 630, w: 1000, h: 460, preco: 'dir', folha: 0.5 },
      { x: 40, y: 1110, w: 1000, h: 460, preco: 'esq', folha: 0.5 }] },
  ],
  3: [
    { nome: 'destaque', rotulo: '1 grande + 2', vagas: [
      { x: 40, y: 630, w: 1000, h: 430, preco: 'dir', folha: 0.46 },
      { x: 25, y: 1080, w: 505, h: 500, preco: 'baixo', lado: 'centro', folha: 0.8 },
      { x: 550, y: 1080, w: 505, h: 500, preco: 'baixo', lado: 'centro', folha: 0.8 }] },
    { nome: 'lista', rotulo: 'Em lista', vagas: [
      { x: 40, y: 630, w: 1000, h: 310, preco: 'dir', folha: 0.46 },
      { x: 40, y: 945, w: 1000, h: 310, preco: 'esq', folha: 0.46 },
      { x: 40, y: 1260, w: 1000, h: 310, preco: 'dir', folha: 0.46 }] },
    { nome: 'tres-colunas', rotulo: 'Três colunas', vagas: [
      { x: 15, y: 760, w: 340, h: 800, preco: 'baixo', lado: 'centro', folha: 1 },
      { x: 370, y: 760, w: 340, h: 800, preco: 'baixo', lado: 'centro', folha: 1 },
      { x: 725, y: 760, w: 340, h: 800, preco: 'baixo', lado: 'centro', folha: 1 }] },
  ],
};

export const disposicaoPadrao = (n) => DISPOSICOES[n]?.[0]?.nome || null;
export const acharDisposicao = (n, nome) => (DISPOSICOES[n] || []).find(d => d.nome === nome) || DISPOSICOES[n]?.[0] || null;

const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const r1 = (v) => Math.round(v * 100) / 100;

/** Altura da folha em % da altura do story. */
export const alturaFolha = (w, folhaProp) => w / folhaProp / PROP;

/**
 * Foto e folha de um produto dentro da vaga. `prop` = altura/largura da foto
 * (já sem a margem transparente). Devolve { area, folha } em % do story.
 */
export function encaixar(v, prop, folhaProp) {
  const fw = v.w * v.folha, fh = fw / folhaProp;
  // Maior foto que cabe no retângulo, mantendo a proporção.
  const caber = (rw, rh) => { let w = rw, h = rw * prop; if (h > rh) { h = rh; w = rh / prop; } return { w, h }; };
  let foto, folha;
  if (v.preco === 'baixo') {
    const sobe = fh * 0.25;                     // a folha sobe um pouco por cima da foto
    const rh = v.h - fh + sobe;
    const { w, h } = caber(v.w, rh);
    foto = { x: v.x + (v.w - w) / 2, y: v.y + rh - h, w, h };
    // Folha encostada no canto da foto (ou no meio dela), sem sair da vaga.
    const fx = v.lado === 'dir' ? foto.x + foto.w - fw * 0.9 : foto.x + (foto.w - fw) / 2;
    folha = { x: limitar(fx, v.x, v.x + v.w - fw), y: v.y + v.h - fh, w: fw };
  } else {
    const entra = fw * 0.12;                    // a folha avança um pouco sobre a foto
    const { w, h } = caber(v.w - fw + entra, v.h);
    // Foto e folha juntas, centradas na vaga.
    const x0 = v.x + (v.w - (w + fw - entra)) / 2;
    const fotoX = v.preco === 'dir' ? x0 : x0 + fw - entra;
    foto = { x: fotoX, y: v.y + (v.h - h) / 2, w, h };
    folha = { x: v.preco === 'dir' ? fotoX + w - entra : x0, y: v.y + (v.h - fh) / 2 + fh * 0.12, w: fw };
  }
  return {
    area: { x: r1(foto.x / STORY_L * 100), y: r1(foto.y / STORY_A * 100), w: r1(foto.w / STORY_L * 100), h: r1(foto.h / STORY_A * 100) },
    folha: { x: r1(folha.x / STORY_L * 100), y: r1(folha.y / STORY_A * 100), w: r1(fw / STORY_L * 100) },
  };
}

/** Recoloca todos os produtos do story nas vagas da disposição (os ajustes à mão saem). */
export function arrumarStory(s, propDe, folhaProp, nome = s.disposicao) {
  const d = acharDisposicao(s.produtos.length, nome);
  if (!d) return { ...s, disposicao: null };
  return {
    ...s, disposicao: d.nome,
    produtos: s.produtos.map((p, i) => ({ ...p, ...encaixar(d.vagas[i], propDe(p.codpro), folhaProp), ajustado: false })),
  };
}

/** Caixa do bloco foto + folha (+ fita do "De:", onde ela estiver), em % do story. */
export function caixaDoProduto(p, folhaProp) {
  const fh = alturaFolha(p.folha.w, folhaProp);
  const comDe = p.mostrarDe && p.de > p.por;
  let x0 = Math.min(p.area.x, p.folha.x), y0 = Math.min(p.area.y, p.folha.y - (comDe && !p.posDe ? fh * 0.15 : 0));
  let x1 = Math.max(p.area.x + p.area.w, p.folha.x + p.folha.w), y1 = Math.max(p.area.y + p.area.h, p.folha.y + fh);
  if (comDe && p.posDe) {
    // Fita arrastada: mede ~metade da folha × 19% dela (a mesma conta do pôster e do catálogo).
    const tx = p.folha.x + p.posDe.x / 100 * p.folha.w, ty = p.folha.y + p.posDe.y / 100 * fh;
    x0 = Math.min(x0, tx); y0 = Math.min(y0, ty); x1 = Math.max(x1, tx + p.folha.w * 0.5); y1 = Math.max(y1, ty + fh * 0.19);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Quanto o bloco pode andar sem a folha sair do story. */
export function limitarDeslocamento(p, dx, dy, folhaProp) {
  const fh = alturaFolha(p.folha.w, folhaProp);
  const de = p.mostrarDe && p.de > p.por ? fh * 0.15 : 0;
  return {
    dx: limitar(dx, -p.folha.x, 100 - p.folha.w - p.folha.x),
    dy: limitar(dy, de - p.folha.y, 100 - fh - p.folha.y),
  };
}

export const moverProduto = (p, dx, dy) => ({
  ...p, ajustado: true,
  area: { ...p.area, x: r1(p.area.x + dx), y: r1(p.area.y + dy) },
  folha: { ...p.folha, x: r1(p.folha.x + dx), y: r1(p.folha.y + dy) },
});

/**
 * Aumenta/diminui o bloco inteiro em volta de um ponto fixo (ax, ay em % do
 * story): o canto oposto ao que a gerente puxa, ou o centro. A escala é limitada
 * pra folha ficar legível e não passar da largura do story.
 */
export function escalarProduto(p, s, ax, ay) {
  const k = limitar(s, 8 / p.folha.w, 95 / p.folha.w);
  const px = (x) => r1(ax + (x - ax) * k), py = (y) => r1(ay + (y - ay) * k);
  return {
    ...p, ajustado: true,
    area: { ...p.area, x: px(p.area.x), y: py(p.area.y), w: r1(p.area.w * k), h: r1(p.area.h * k) },
    folha: { ...p.folha, x: px(p.folha.x), y: py(p.folha.y), w: r1(p.folha.w * k) },
  };
}

/** Só a foto ou só a folha, em volta do próprio centro. */
export function tamanhoDaFoto(p, w) {
  const k = limitar(w, 5, 160) / p.area.w;
  const cx = p.area.x + p.area.w / 2, cy = p.area.y + p.area.h / 2;
  const nw = p.area.w * k, nh = p.area.h * k;
  return { ...p, ajustado: true, area: { ...p.area, x: r1(cx - nw / 2), y: r1(cy - nh / 2), w: r1(nw), h: r1(nh) } };
}
export function tamanhoDaFolha(p, w, folhaProp) {
  const nw = limitar(w, 8, 95);
  const h0 = alturaFolha(p.folha.w, folhaProp), h1 = alturaFolha(nw, folhaProp);
  const x = limitar(p.folha.x + (p.folha.w - nw) / 2, 0, 100 - nw);
  const y = limitar(p.folha.y + (h0 - h1) / 2, 0, 100 - h1);
  return { ...p, ajustado: true, folha: { x: r1(x), y: r1(y), w: r1(nw) } };
}

/**
 * A foto chegou (ou mudou de proporção): produto ainda no lugar da disposição
 * é reencaixado; o que a gerente já mexeu só corrige a altura, mantendo largura
 * e centro.
 */
export function corrigirProporcao(s, codpro, prop, folhaProp) {
  if (!s.produtos.some(p => p.codpro === codpro)) return s;
  const d = acharDisposicao(s.produtos.length, s.disposicao);
  return {
    ...s,
    produtos: s.produtos.map((p, i) => {
      if (p.codpro !== codpro) return p;
      if (!p.ajustado && d) return { ...p, ...encaixar(d.vagas[i], prop, folhaProp) };
      const h = r1(p.area.w * prop / PROP);
      return { ...p, area: { ...p.area, y: r1(p.area.y + (p.area.h - h) / 2), h } };
    }),
  };
}
